import { apiEndpointToBaseUrl } from 'utils/apiEndpointToFullUrl';

/*
 * Helpers to detect that the API server is unavailable, e.g. during system upgrade or restart,
 * when API calls get HTML (upgrade page) or gateway errors instead of API responses.
 * The state itself lives in GlobalState (`systemUnavailable`).
 */

export type ApiProbeResult =
  | { available: false }
  | { available: true; version: string | null };

/**
 * Any JSON response (regardless of status, e.g. 401) means the API server is running.
 * HTML (upgrade page), gateway errors without JSON or network errors mean it is not.
 */
export const probeApi = async (): Promise<ApiProbeResult> => {
  try {
    const response = await fetch(apiEndpointToBaseUrl('/os/version'), {
      cache: 'no-store',
    });
    if (!response.headers.get('Content-Type')?.includes('json')) {
      return { available: false };
    }
    const body: unknown = await response.json().catch(() => null);
    const version =
      response.ok &&
      typeof body === 'object' &&
      body !== null &&
      'version' in body &&
      typeof body.version === 'string'
        ? body.version
        : null;
    return { available: true, version };
  } catch {
    return { available: false };
  }
};

let pendingProbe: Promise<ApiProbeResult> | null = null;

/**
 * Same as `probeApi`, but concurrent callers share one probe,
 * e.g. when many requests fail at once at the start of an outage.
 */
export const probeApiShared = (): Promise<ApiProbeResult> => {
  pendingProbe ??= probeApi().finally(() => {
    pendingProbe = null;
  });
  return pendingProbe;
};

/**
 * Whether the API runs a different version than the loaded frontend, so the page has to be reloaded.
 * Unknown version (e.g. 401 when the access token expired during the outage) does not count,
 * a new version is still announced by the socket `version` event (refresh dialog).
 */
export const isNewVersionRunning = (
  probedVersion: string | null,
  loadedVersion: string | null,
): boolean => probedVersion !== null && probedVersion !== loadedVersion;

/** HTTP statuses returned by nginx when the API server is down or restarting */
export const isGatewayErrorStatus = (status: number): boolean =>
  status === 502 || status === 503 || status === 504;

export const isHtmlContentType = (contentType: string | null): boolean =>
  contentType?.includes('text/html') ?? false;
